import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Building2, Plus, Edit2, Trash2, MapPin, Phone, Clock, DollarSign } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import ConfirmDialog from './ConfirmDialog';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function BranchManagement() {
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchBranches();
  }, []);

  const fetchBranches = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/branches?include_inactive=true`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.data.success) {
        setBranches(response.data.branches);
      }
    } catch (error) {
      console.error('Error fetching branches:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (branch) => {
    setSelectedBranch(branch);
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirmed = async () => {
    if (!selectedBranch) return;

    try {
      const response = await axios.delete(
        `${API_URL}/api/branches/${selectedBranch.branch_id}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        fetchBranches();
        toast.success('Cabang berhasil dihapus!', {
          description: 'Data cabang telah dihapus dari sistem',
          duration: 3000
        });
      }
    } catch (error) {
      toast.error('Gagal menghapus cabang', {
        description: error.response?.data?.detail || 'Terjadi kesalahan saat menghapus cabang',
        duration: 4000
      });
    } finally {
      setSelectedBranch(null);
    }
  };

  const handleEditClick = (branch) => {
    setSelectedBranch(branch);
    setShowEditModal(true);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(value);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Management Cabang</h2>
          <p className="text-sm text-gray-600">Kelola data cabang dan harga rental</p>
        </div>
        <Button
          onClick={() => setShowCreateModal(true)}
          className="bg-green-600 hover:bg-green-700 text-white"
        >
          <Plus className="h-4 w-4 mr-2" />
          Tambah Cabang
        </Button>
      </div>

      {/* Branches Grid */}
      {loading ? (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Memuat data...</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {branches.map((branch) => (
            <Card key={branch.branch_id} className={!branch.is_active ? 'opacity-50' : ''}>
              <CardHeader className="pb-3">
                <div className="flex justify-between items-start">
                  <div className="flex items-center gap-2">
                    <Building2 className="h-5 w-5 text-blue-600" />
                    <CardTitle className="text-lg">{branch.name}</CardTitle>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleEditClick(branch)}
                      className="h-8 w-8 p-0"
                    >
                      <Edit2 className="h-4 w-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDeleteClick(branch)}
                      className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-start gap-2 text-sm text-gray-600">
                  <MapPin className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <span>{branch.address || '-'}</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Phone className="h-4 w-4 flex-shrink-0" />
                  <span>{branch.phone || '-'}</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Clock className="h-4 w-4 flex-shrink-0" />
                  <span>{branch.operating_hours || '-'}</span>
                </div>
                <div className="flex items-center gap-2 pt-2 border-t">
                  <DollarSign className="h-5 w-5 text-green-600" />
                  <div>
                    <p className="text-xs text-gray-500">Harga per Jam</p>
                    <p className="text-lg font-bold text-green-600">
                      {formatCurrency(branch.price_per_hour)}
                    </p>
                  </div>
                </div>
                {!branch.is_active && (
                  <div className="mt-2 px-2 py-1 bg-red-100 text-red-700 text-xs rounded text-center">
                    Tidak Aktif
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <BranchFormModal
          mode="create"
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false);
            fetchBranches();
          }}
          token={token}
        />
      )}

      {/* Edit Modal */}
      {showEditModal && selectedBranch && (
        <BranchFormModal
          mode="edit"
          branch={selectedBranch}
          onClose={() => {
            setShowEditModal(false);
            setSelectedBranch(null);
          }}
          onSuccess={() => {
            setShowEditModal(false);
            setSelectedBranch(null);
            fetchBranches();
          }}
          token={token}
        />
      )}

      {/* Delete Confirmation */}
      <ConfirmDialog
        open={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false);
          setSelectedBranch(null);
        }}
        onConfirm={handleDeleteConfirmed}
        title="Konfirmasi Hapus Cabang"
        description={`Apakah Anda yakin ingin menghapus cabang "${selectedBranch?.name}"? Pastikan tidak ada karyawan atau smart plug yang masih terdaftar di cabang ini.`}
        confirmText="Ya, Hapus"
        cancelText="Batal"
        variant="danger"
      />
    </div>
  );
}

// Branch Form Modal Component
function BranchFormModal({ mode, branch, onClose, onSuccess, token }) {
  const [formData, setFormData] = useState({
    name: branch?.name || '',
    address: branch?.address || '',
    phone: branch?.phone || '',
    price_per_hour: branch?.price_per_hour || 10000,
    operating_hours: branch?.operating_hours || '',
    is_active: branch?.is_active ?? true
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const url = mode === 'create'
        ? `${API_URL}/api/branches`
        : `${API_URL}/api/branches/${branch.branch_id}`;
      
      const method = mode === 'create' ? 'post' : 'put';
      
      const response = await axios[method](url, formData, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.data.success) {
        onSuccess();
      }
    } catch (error) {
      setError(error.response?.data?.detail || 'Gagal menyimpan cabang');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {mode === 'create' ? 'Tambah Cabang Baru' : 'Edit Cabang'}
          </DialogTitle>
          <DialogDescription>
            {mode === 'create' 
              ? 'Isi data cabang baru yang akan ditambahkan'
              : 'Update data cabang'}
          </DialogDescription>
        </DialogHeader>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="name">Nama Cabang *</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              placeholder="Contoh: Cabang Pusat"
              required
            />
          </div>

          <div>
            <Label htmlFor="address">
              Alamat Cabang <span className="text-blue-600 text-xs">(untuk struk/laporan)</span>
            </Label>
            <textarea
              id="address"
              value={formData.address}
              onChange={(e) => setFormData({...formData, address: e.target.value})}
              placeholder="Contoh: Jl. Sudirman No. 123, Jakarta Pusat"
              rows="2"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <p className="text-xs text-gray-500 mt-1">Alamat ini akan muncul di struk pelanggan</p>
          </div>

          <div>
            <Label htmlFor="phone">
              No. Telepon Cabang <span className="text-blue-600 text-xs">(untuk struk/laporan)</span>
            </Label>
            <Input
              id="phone"
              value={formData.phone}
              onChange={(e) => setFormData({...formData, phone: e.target.value})}
              placeholder="Contoh: 0812-3456-7890"
            />
            <p className="text-xs text-gray-500 mt-1">Nomor telepon ini akan muncul di struk pelanggan</p>
          </div>

          <div>
            <Label htmlFor="price">Harga per Jam (Rp) *</Label>
            <Input
              id="price"
              type="number"
              value={formData.price_per_hour}
              onChange={(e) => setFormData({...formData, price_per_hour: parseInt(e.target.value)})}
              min="1000"
              step="1000"
              required
            />
          </div>

          <div>
            <Label htmlFor="hours">Jam Operasional</Label>
            <Input
              id="hours"
              value={formData.operating_hours}
              onChange={(e) => setFormData({...formData, operating_hours: e.target.value})}
              placeholder="09:00 - 22:00"
            />
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="is_active"
              checked={formData.is_active}
              onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
              className="w-4 h-4"
            />
            <Label htmlFor="is_active" className="cursor-pointer">Cabang Aktif</Label>
          </div>

          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Batal
            </Button>
            <Button type="submit" disabled={loading} className="bg-blue-600 hover:bg-blue-700">
              {loading ? 'Menyimpan...' : 'Simpan'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
